<div *ngIf="isLoading" class="vb-spinner-box vb-spinner-box--page">
    <vb-spinner size="xl" label="Loading lot"></vb-spinner>
</div>

<div *ngIf="unableToLoad" class="d-flex h-100 p-5 m-5">
    <h1 class="m-auto">{{ "LotDetails.unableToLoadLot" | translate }}</h1>
</div>

<div *ngIf="!isLoading && !unableToLoad" class="vb-lot-details">
    <!-- search + jump-to-lot row start -->
    <div class="lot-search-row">
        <vb-search
            class="lot-search-row__search"
            placeholder="Search for items in this auction"
            ariaLabel="Search for items in this auction" />
        <vb-lot-jump [auctionId]="auctionId" />
    </div>
    <!-- search + jump-to-lot row end -->

    <vb-breadcrumb
        class="vb-lot-breadcrumb"
        [trail]="crumbs"
        [showBack]="true"
        (back)="back()"
        (crumbSelect)="gotoCrumb($event)">
        <!-- Prev/next lot in catalog order; a side without a neighbour
             renders disabled. Hidden until the adjacency fetch lands. -->
        <div *ngIf="adjacentLots" vbBreadcrumbActions class="vb-lot-adjacent">
            <button
                type="button"
                class="vb-lot-adjacent__btn"
                [disabled]="!adjacentLots.previous"
                aria-label="Previous lot"
                (click)="goToAdjacentLot(adjacentLots.previous)">
                <vb-icon name="chevLeft" [size]="16" [strokeWidth]="2" />
                <span class="vb-lot-adjacent__label">{{
                    adjacentLotLabel(adjacentLots.previous, "Prev")
                }}</span>
            </button>
            <button
                type="button"
                class="vb-lot-adjacent__btn"
                [disabled]="!adjacentLots.next"
                aria-label="Next lot"
                (click)="goToAdjacentLot(adjacentLots.next)">
                <span class="vb-lot-adjacent__label">{{
                    adjacentLotLabel(adjacentLots.next, "Next")
                }}</span>
                <vb-icon name="chevRight" [size]="16" [strokeWidth]="2" />
            </button>
        </div>
    </vb-breadcrumb>

    <!-- Lot stays visible once closed; the "Closed" chip, hidden bid actions
         and disabled bid panel put the page in a locked, shut-out state. -->
    <ng-container>
        <div class="vb-lot-grid">
            <!-- Gallery column: scrolls with the page -->
            <div class="vb-gallery-col">
                <vb-gallery
                    [photos]="mappedLot.images || []"
                    [leadPhotoId]="mappedLot.thumbnailId || null"
                    [lotCode]="'LOT ' + (mappedLot.number || '')"
                    [lotTitle]="mappedLot.title"
                    [watched]="watched"
                    [showShare]="false"
                    (watchToggled)="toggleWatch()" />
            </div>

            <!-- Buy box: actions, heading, chips, bid panel (sticky on desktop via SCSS) -->
            <div class="vb-buybox">
                <div class="vb-actions" *ngIf="isLoggedIn && !isClosed">
                    <vb-icon-button
                        icon="history"
                        [tipPosition]='"bottom"'
                        [label]="'LotDetails.lotHistory' | translate"
                        [active]="activeDialog === 'history'"
                        (clicked)="open('history')" />
                    <vb-icon-button
                        icon="note"
                        [tipPosition]='"bottom"'
                        [label]="'LotDetails.notes' | translate"
                        [active]="activeDialog === 'notes'"
                        (clicked)="open('notes')" />
                    <vb-icon-button
                        icon="dollar"
                        [tipPosition]='"bottom"'
                        [label]="'LotDetails.priceAlerts' | translate"
                        [active]="activeDialog === 'alerts'"
                        (clicked)="open('alerts')" />
                    <vb-icon-button
                        *ngIf="showMakeAnOffer"
                        icon="tag"
                        [tipPosition]='"bottom"'
                        [label]="'LotDetails.makeOffer' | translate"
                        [active]="activeDialog === 'offer'"
                        (clicked)="open('offer')" />
                    <vb-icon-button
                        icon="message"
                        [tipPosition]='"bottom"'
                        [label]="'LotDetails.messageSeller' | translate"
                        (clicked)="sendMessageToSeller()" />
                </div>

                <div class="vb-lot-heading">
                    <p class="vb-mono-eyebrow" *ngIf="lotItem.number !== null">
                        LOT {{ lotItem.number }}{{ lotItem.suffix || "" }}
                    </p>
                    <h1 class="vb-title-lg vb-lot-title">
                        {{ lotItem.title }}
                    </h1>
                </div>

                <div class="vb-chips">
                    <vb-chip [tone]="statusChip.tone">{{ statusChip.label }}</vb-chip>
                    <vb-chip tone="neutral" *ngIf="mappedLot.condition">{{
                        mappedLot.condition
                    }}</vb-chip>
                    <vb-chip [tone]="reserveChip.tone" *ngIf="showReserveChip">{{
                        reserveChip.label
                    }}</vb-chip>
                    <vb-chip tone="neutral" *ngIf="mappedAuction.buyerPremium">
                        +{{ mappedAuction.buyerPremium }}% BP
                    </vb-chip>
                </div>

                <vb-bid-panel
                    #bidPanel
                    [lot]="mappedLot"
                    [auction]="mappedAuction"
                    [currentUserId]="currentUserId || null"
                    [secondsLeft]="secondsLeft"
                    [biddingEnded]="biddingEnded"
                    [userHasBid]="userHasBid"
                    [bidLoading]="bidLoading"
                    [closesAtLabel]="closesAtLabel"
                    [opensAtLabel]="opensAtLabel"
                    (quickBid)="onQuickBid()"
                    (proxyBid)="onProxyBid($event)"
                    (bidRounded)="onBidRounded()"
                    (signIn)="goSignIn()"
                    (remind)="onRemind()" />
            </div>

            <!-- Details column: specs + description, scrolls with the page -->
            <div class="vb-details-col">
                <div class="vb-specs">
                    <vb-spec-row [label]="'LotDetails.seller' | translate"
                        >ViewBid Auctions</vb-spec-row>
                    <vb-spec-row [label]="'LotDetails.endDate' | translate" *ngIf="closesAtLabel">{{
                        closesAtLabel
                    }}</vb-spec-row>
                    <vb-spec-row [label]="'LotDetails.category' | translate" *ngIf="mappedLot.category">{{
                        mappedLot.category
                    }}</vb-spec-row>
                    <vb-spec-row [label]="'LotDetails.condition' | translate" *ngIf="mappedLot.condition">{{
                        mappedLot.condition
                    }}</vb-spec-row>
                    <vb-spec-row [label]="'LotDetails.watcher' | translate">{{
                        lotItem.numberOfWatchers || 0
                    }}</vb-spec-row>
                    <vb-spec-row [label]="'LotDetails.bids' | translate">
                        {{ lotItem.bidCount || 0 }}
                        <button
                            *ngIf="canViewBidHistory"
                            type="button"
                            class="vb-bid-history-link"
                            (click)="open('bidHistory')">
                            {{ "LotDetails.viewBidHistory" | translate }}
                        </button>
                    </vb-spec-row>
                    <vb-spec-row
                        class="vb-highbidder-row"
                        [label]="'LotDetails.highestBidder' | translate"
                        *ngIf="highestBidder">
                        <span class="vb-masked">{{ highestBidder }}</span>
                        <span *ngIf="isProxyBid" class="vb-proxy-tag">(Proxy)</span>
                    </vb-spec-row>
                    <vb-spec-row *ngFor="let attribute of attributes" [label]="attribute.label || ''">{{
                        attribute.value
                    }}</vb-spec-row>
                </div>

                <div class="vb-description" *ngIf="lotItem.description">
                    <h4 class="vb-description-title">
                        {{ "LotDetails.description" | translate }}
                    </h4>
                    <div [innerHTML]="sanitizedDescription"></div>
                </div>

                <div class="vb-documents" *ngIf="documentItems.length">
                    <h4 class="vb-documents-title">
                        {{ "LotDetails.documents" | translate }}
                    </h4>
                    <vb-document-list
                        [documents]="documentItems"
                        [downloadingId]="downloadingDocId"
                        (download)="onDownloadDocument($event)" />
                </div>
            </div>
        </div>
    </ng-container>
</div>

<!-- Bid confirmation (library dialog) -->
<vb-bid-confirm-dialog
    [open]="activeDialog === 'confirm'"
    [amount]="pendingBidAmount"
    [bpPct]="mappedAuction.buyerPremium || 0"
    (confirmed)="onBidConfirmed($event)"
    (cancelled)="closeDialog()" />

<!-- Sub-features, re-homed onto the dialog shell -->
<vb-dialog
    [open]="activeDialog === 'history'"
    [eyebrow]="lotCodeLabel"
    [title]="'LotDetails.lotHistory' | translate"
    (closed)="closeDialog()">
    <vb-lot-edit-history
        [lotEditHistory]="lotEditHistory"
        (closeEditHistory)="closeDialog()" />
    <vb-button
        dialogFooter
        variant="secondary"
        [fullWidth]="true"
        (clicked)="closeDialog()">
        {{ "LotDetails.close" | translate }}
    </vb-button>
</vb-dialog>

<vb-dialog
    [open]="activeDialog === 'bidHistory'"
    [eyebrow]="lotCodeLabel"
    [title]="'LotDetails.bidHistory' | translate"
    [showClose]="true"
    (closed)="closeDialog()">
    <app-bid-history-table
        *ngIf="activeDialog === 'bidHistory'"
        [auctionLotsId]="lotItem.auctionLotsId"
        [showHistory]="activeDialog === 'bidHistory'"
        (hideHistory)="closeDialog()" />
    <vb-button
        dialogFooter
        variant="secondary"
        [fullWidth]="true"
        (clicked)="closeDialog()">
        {{ "LotDetails.close" | translate }}
    </vb-button>
</vb-dialog>

<vb-dialog
    [open]="activeDialog === 'notes'"
    [eyebrow]="lotCodeLabel"
    [title]="'LotDetails.notes' | translate"
    (closed)="closeDialog()">
    <vb-lot-item-user-notes
        *ngIf="activeDialog === 'notes'"
        [lotItemUserNotes]="lotItemUserNotes"
        [lotId]="lotItem.id || ''"
        (closeNotes)="closeDialog()"
        (addNote)="addNotes($event)"
        (removeNote)="removeNote($event)"
        (updateNote)="updateNote($event)" />
    <vb-button
        dialogFooter
        variant="secondary"
        [fullWidth]="true"
        (clicked)="closeDialog()">
        {{ "LotDetails.close" | translate }}
    </vb-button>
</vb-dialog>

<vb-dialog
    [open]="activeDialog === 'alerts'"
    [eyebrow]="lotCodeLabel"
    [title]="'LotDetails.priceAlerts' | translate"
    (closed)="closeDialog()">
    <vb-lot-item-alerts
        *ngIf="activeDialog === 'alerts'"
        [lot]="lotItem"
        (remindersChange)="onRemindersChange($event)"
        (closeReminders)="closeDialog()" />
    <vb-button
        dialogFooter
        variant="secondary"
        [fullWidth]="true"
        (clicked)="closeDialog()">
        {{ "LotDetails.close" | translate }}
    </vb-button>
</vb-dialog>

<vb-dialog
    [open]="activeDialog === 'offer'"
    [eyebrow]="lotCodeLabel"
    [title]="'LotDetails.makeOffer' | translate"
    (closed)="closeDialog()">
    <vb-make-offer
        *ngIf="activeDialog === 'offer'"
        [lot]="lotItem"
        [offerId]="offerId || ''"
        (closeOfferSection)="closeDialog()" />
    <vb-button
        dialogFooter
        variant="secondary"
        [fullWidth]="true"
        (clicked)="closeDialog()">
        {{ "LotDetails.close" | translate }}
    </vb-button>
</vb-dialog>
